import { computed, effect, untracked, type ReadonlySignal, type Signal } from "@preact/signals";

import { type LiveData, type LogData, type Pos } from "../src/data";

import { type CleanupFunction, type DrawFunction } from "./render-all";
import useTheme from "./theme";

interface CanvasRenderProps {
	liveData: ReadonlySignal<LiveData>,
	loggedData: ReadonlySignal<LogData | undefined>,
	liveDataUpdate: Signal<Pos[]>,
}

const theme = useTheme();

export default function createCanvasRender({loggedData, liveData, liveDataUpdate}: CanvasRenderProps): DrawFunction {
	return function canvasRender(dCtx) {
		const { ctx, view, clear, forceRedraw } = dCtx;

		const activeData = computed(() => loggedData.value != undefined ? loggedData.value : liveData.value);
		const isLive = computed(() => loggedData.value == undefined);

		function drawDataPoint(pos: Pos, size: number) {
			ctx.beginPath();
			ctx.arc(...dCtx.scaled(pos.x, pos.y), Math.max(size / view.zoom.value, 0.4), 0, 2 * Math.PI);
			ctx.fill();
		}

		const show = 30;
		const min = 0x10;
		const max = 0xf0;

		function drawActiveData() {
			const d = activeData.value;

			if (isLive.value) {
				// fade out old data points
				const len = d.data.length;
				ctx.fillStyle = `${theme.red}${min.toString(16)}`;
				d.data.forEach((e, i) => {
					if (i < (len - 2000)) {
						return;
					}
					if (i > (len - show)) {
						const opacity = (max - (max - min) * (len - i)/show);
						ctx.fillStyle = `${theme.red}${Math.round(opacity).toString(16)}`;
					}
					if (i == (len - 1)) {
						ctx.fillStyle = theme.blue;
					}
					drawDataPoint(e.pos, i == (len - 1) ? 5 : 3); 
				});
			} else {
				ctx.fillStyle = theme.red;
				d.data.forEach((e) => {
					drawDataPoint(e.pos, 3); 
				});
			}

			return () => {
				clear();
			};
		}

		function drawLiveDataUpdates() {
			const upd = liveDataUpdate.value;
			if (!isLive.value || upd.length == 0) {
				return;
			}

			untracked(() => {
				clear();
				drawActiveData();
			});

			ctx.fillStyle = theme.blue;
			upd.forEach((p) => {
				drawDataPoint(p, 5);
			});

			liveDataUpdate.value = [];

			// return () => {
			// 	// instead of clearing, partially fade the previous frame
								
			// 	ctx.save();
			// 	ctx.globalCompositeOperation = "destination-in";
			// 	ctx.setTransform(1, 0, 0, 1, 0, 0);
			// 	ctx.fillStyle = "rgba(255, 255, 255, 0.9";
			// 	ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
			// 	ctx.restore();
			// };
		}
		const drawers: (() => void)[] = [	drawActiveData, drawLiveDataUpdates ];

		const effects: CleanupFunction[] = [
			...drawers,
			function forcedRedraw() { // forceRedraw signal
				const _ = forceRedraw.value;

				return () => {
					const redraw = forceRedraw.peek();
					console.log("forced redraw, clear?", redraw.clear);

					if (redraw.clear) {
						clear();
					}

					untracked(() => {
						drawers.forEach((d) => {
							d(); 
						});
					});
				};
			},
		].map((f) => effect(f));

		return () => {
			effects.forEach((e) => {
				e(); 
			});
		};
	};
}
